import { useState, useEffect } from 'react';
import { CategoriaWithSubcategorias } from '../types/categoria.types';

const CACHE_KEY = 'categorias_cache';
const CACHE_TIME_MS = 60 * 60 * 1000; // 1 hora
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

export function useCategorias() {
  const [categorias, setCategorias] = useState<CategoriaWithSubcategorias[]>([]);
  const [filteredCategorias, setFilteredCategorias] = useState<CategoriaWithSubcategorias[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cacheTime, setCacheTime] = useState<number | null>(null);

  // Fetch árbol completo
  const fetchTree = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/categorias/tree`);
      if (!response.ok) throw new Error('Error fetching categories');

      const data = await response.json();
      setCategorias(data.data);
      setFilteredCategorias(data.data);
      setCacheTime(Date.now());
      localStorage.setItem(CACHE_KEY, JSON.stringify({ data: data.data, time: Date.now() }));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos al montar
  useEffect(() => {
    // Intentar cargar del cache primero
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { data, time } = JSON.parse(cached);
        if (Date.now() - time < CACHE_TIME_MS) {
          setCategorias(data);
          setFilteredCategorias(data);
          setCacheTime(time);
          return;
        }
      } catch (err) {
        // Cache inválido, continuar con fetch
      }
    }

    fetchTree();
  }, []);

  // Búsqueda con debounce
  useEffect(() => {
    if (searchQuery.trim()) {
      const timer = setTimeout(() => {
        performSearch(searchQuery);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setFilteredCategorias(categorias);
    }
  }, [searchQuery, categorias]);

  const performSearch = async (query: string) => {
    try {
      const response = await fetch(`${API_URL}/api/categorias/search?q=${encodeURIComponent(query)}`);
      if (!response.ok) throw new Error('Search error');

      const data = await response.json();
      setFilteredCategorias(data.data);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const createCategoria = async (data: any) => {
    try {
      const response = await fetch(`${API_URL}/api/categorias`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error creating category');
      }

      await fetchTree();
      return await response.json();
    } catch (err) {
      setError((err as Error).message);
      throw err;
    }
  };

  const updateCategoria = async (id: number, data: any) => {
    try {
      const response = await fetch(`${API_URL}/api/categorias/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Error updating category');

      await fetchTree();
      return await response.json();
    } catch (err) {
      setError((err as Error).message);
      throw err;
    }
  };

  const deleteCategoria = async (id: number) => {
    try {
      const response = await fetch(`${API_URL}/api/categorias/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Error deleting category');

      await fetchTree();
    } catch (err) {
      setError((err as Error).message);
      throw err;
    }
  };

  const createSubcategoria = async (categoriaId: number, data: any) => {
    try {
      const response = await fetch(`${API_URL}/api/categorias/${categoriaId}/subcategorias`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error creating subcategory');
      }

      await fetchTree();
      return await response.json();
    } catch (err) {
      setError((err as Error).message);
      throw err;
    }
  };

  const updateSubcategoria = async (categoriaId: number, subId: number, data: any) => {
    try {
      const response = await fetch(`${API_URL}/api/categorias/${categoriaId}/subcategorias/${subId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Error updating subcategory');

      await fetchTree();
      return await response.json();
    } catch (err) {
      setError((err as Error).message);
      throw err;
    }
  };

  const deleteSubcategoria = async (categoriaId: number, subId: number) => {
    try {
      const response = await fetch(`${API_URL}/api/categorias/${categoriaId}/subcategorias/${subId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.productosCount) {
          throw new Error(`Cannot delete: ${errorData.productosCount} products associated`);
        }
        throw new Error(errorData.error || 'Error deleting subcategory');
      }

      await fetchTree();
    } catch (err) {
      setError((err as Error).message);
      throw err;
    }
  };

  return {
    categorias,
    filteredCategorias,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    fetchTree,
    performSearch,
    createCategoria,
    updateCategoria,
    deleteCategoria,
    createSubcategoria,
    updateSubcategoria,
    deleteSubcategoria,
    refetch: fetchTree,
    isCached: cacheTime ? Date.now() - cacheTime < CACHE_TIME_MS : false,
    cacheTime
  };
}
